import LibraryMusicIcon from "@/assets/icons/Library audiolab music.svg?react";
import { AlbumLink } from "@/components/links/AlbumLink";
import ExclusiveReleaseBadge from "@/components/ui/ExclusiveReleaseBadge";
import { DetailAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { buttonTokens } from "@/lib/tokens";
import { albumTypeLabel, cn, isNewRelease, truncateText } from "@/lib/utils";
import { Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { HiOutlineUser } from "react-icons/hi";
import { CreatorLink } from "../../links/CreatorLink";

type FeaturedAlbumBannerProps = {
  album: DetailAlbumDto;
  description?: string;
  className?: string;
  isExclusive?: boolean;
  /** Override the creator ID used for the Artist Profile link */
  creatorIdOverride?: string;
  /** Override the displayed artist name (instead of creator.stageName) */
  stageNameOverride?: string;
  /** Override the label text (defaults to the release type, "New …" for a week) */
  labelOverride?: string;
  /** Override the banner background image (instead of album artwork) */
  bannerImageUrl?: string;
  /** Override the image object-position (default: "center 65%") */
  imagePosition?: string;
  /** Animate the image with a slow pan effect */
  animateImage?: boolean;
};

export function FeaturedAlbumBanner({
  album,
  description,
  className,
  isExclusive = false,
  creatorIdOverride,
  stageNameOverride,
  labelOverride,
  bannerImageUrl,
  imagePosition,
  animateImage,
}: FeaturedAlbumBannerProps) {
  const displayName = stageNameOverride ?? album.creator.stageName;
  const displayDescription = description ?? album.description;
  const label =
    labelOverride ??
    `${isNewRelease(album) ? "New " : ""}${albumTypeLabel(album)}`;

  const { truncated: truncatedDescription, isTruncated } = useMemo(
    () => truncateText(displayDescription, 180),
    [displayDescription],
  );

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-lg lg:aspect-[8/3] lg:max-h-[480px]",
        className,
      )}
    >
      {/* Background Image - Mobile (always uses album artwork) */}
      {album.artworkUrl && (
        <img
          src={album.artworkUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover md:hidden"
          style={{ objectPosition: "center 65%" }}
        />
      )}

      {/* Background Image - Desktop lg+ (uses banner image or artwork fallback) */}
      {(bannerImageUrl || album.artworkUrl) && (
        <img
          src={bannerImageUrl || album.artworkUrl!}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover hidden md:block"
          style={
            animateImage
              ? {
                  objectPosition: "center 10%",
                  animation: "slowPan 8s ease-in-out forwards",
                }
              : { objectPosition: imagePosition ?? "center 65%" }
          }
        />
      )}

      {/* Readability Overlay (full banner coverage) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/45 to-black/75" />

      {/* Exclusive Badge */}
      {isExclusive && (
        <ExclusiveReleaseBadge className="absolute top-5 left-5 sm:top-8 sm:left-8 lg2:top-10 lg2:left-10 z-20" />
      )}

      {/* Mobile Content */}
      <AlbumLink
        album={album}
        className="relative z-10 w-full aspect-square p-4 pb-10 flex justify-between items-end sm:hidden [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]"
      >
        <Flex direction="column" className="gap-1">
          <Text className="font-open-sans text-xs font-semibold text-white/95 uppercase tracking-wide">
            {label}
          </Text>
          <Text className="font-figtree text-3xl font-bold text-white leading-tight">
            {album.title}
          </Text>
          <Text className="font-figtree text-lg font-semibold text-white/95 leading-tight">
            {displayName}
          </Text>
        </Flex>
      </AlbumLink>

      {/* Desktop Content */}
      <Flex className="relative z-10 w-full h-full hidden sm:flex sm:min-h-[340px] lg:min-h-0 sm:p-8 lg2:p-10 justify-between items-end [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]">
        <Flex direction="column" className="gap-4 max-w-full">
          <Flex direction="column" className="gap-1">
            <Text className="font-open-sans text-sm font-semibold text-white/95 uppercase tracking-wide">
              {label}
            </Text>
            <Text className="font-figtree text-3xl lg2:text-4xl font-bold text-white leading-tight">
              {album.title}
            </Text>
            <Text className="font-figtree text-xl lg2:text-2xl font-semibold text-white/95 leading-tight">
              by {displayName}
            </Text>
          </Flex>

          <Flex className="gap-2">
            <AlbumLink
              album={album}
              className={cn(buttonTokens.heroPrimary, "[text-shadow:none]")}
            >
              <LibraryMusicIcon className="w-5 h-5" />
              View Album
            </AlbumLink>
            <CreatorLink
              creator={album.creator}
              creatorIdOverride={creatorIdOverride}
              className={cn(buttonTokens.heroSecondary, "[text-shadow:none]")}
            >
              <HiOutlineUser className="w-5 h-5" />
              Artist Profile
            </CreatorLink>
          </Flex>
        </Flex>

        {displayDescription && (
          <Flex className="hidden lg2:flex max-w-[400px] items-end">
            <Text className="text-left text-sm font-normal leading-[140%] text-white/95">
              {truncatedDescription}
              {isTruncated && (
                <>
                  ...{" "}
                  <Link
                    to="/albums/$ALBUM_ID"
                    params={{ ALBUM_ID: album.albumId }}
                    className="cursor-pointer text-sm font-normal leading-[140%] text-white/95 underline transition-opacity duration-150 hover:opacity-80"
                    style={{
                      textDecorationSkipInk: "auto",
                      textUnderlinePosition: "from-font",
                    }}
                  >
                    read more
                  </Link>
                </>
              )}
            </Text>
          </Flex>
        )}
      </Flex>
    </div>
  );
}
